'use client'
import { removeUser } from '@/app/(main)/agency/(auth)/helper'
import { handleApiError } from '@/utils/utils'
import axios from 'axios'

export type Invitation = {
  email: string
  full_name: string
  roleName: string
}

// Keep the original apiClient for other endpoints
import { ENV } from '@/config/env'
import { STORAGE_KEYS } from '@/constants/storage/keys'
import { clearApiClients, getAxiosApiClientByUser } from './axiosInstance'

export const signInWithEmail = async (payload: {
  email: string
  password: string
}) => {
  try {
    const apiClient = await getAxiosApiClientByUser(payload.email)
    const response = await apiClient.post('/auth/login', payload)
    // console.log(response, 'response from login')
    return response.data // Return only the relevant data
  } catch (error) {
    handleApiError(error, 'Log-in failed')
  }
}

export const signOut = async () => {
  try {
    console.warn('[SIGNOUT] Getting axios client for signOut');
    const apiClient = await getAxiosApiClientByUser();
    console.warn('[SIGNOUT] Making logout API request');
    const response = await apiClient.post('/auth/logout');
    console.warn('[SIGNOUT] Logout API response:', response.status);
    
    // Record the logout debug ID before clearing clients
    const logoutDebugId = localStorage.getItem('logout_debug_id');
    
    clearApiClients();
    console.warn('[SIGNOUT] API clients cleared');

    removeUser();
    console.warn('[SIGNOUT] User removed from localStorage');
    
    // Set an event that components can listen for
    localStorage.setItem('logout_event', Date.now().toString());
    console.warn('[SIGNOUT] Logout event set with debug ID:', logoutDebugId);
    
    return response;
  } catch (error) {
    console.error('[SIGNOUT] Logout API error:', error);
    handleApiError(error, 'Log out Failed');
  }
}

export const signUp = async (formData: FormData) => {
  try {
    const apiClient = await getAxiosApiClientByUser(
      formData.get('email') as string
    )
    const response = await apiClient.post('/auth/sign-up', formData)
    return response.data
  } catch (error) {
    handleApiError(error, 'Sign-up failed')
  }
}

export const initialSignup = async (payload: {
  email: string
  password: string
  dataStorageLocation?: string
  agencyId?: string
}) => {
  try {
    // Create FormData to match FastAPI endpoint expectations
    const formData = new FormData()
    formData.append('email', payload.email)
    formData.append('password', payload.password)
    if (payload.agencyId) {
      formData.append('agencyId', payload.agencyId)
    }
    if (payload.dataStorageLocation) {
      formData.append('dataStorageLocation', payload.dataStorageLocation)
    }

    const apiClient = await getAxiosApiClientByUser(
      payload.email,
      payload.dataStorageLocation
    )

    const response = await apiClient.post('/auth/initial-signup', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  } catch {
    // console.error('Initial sign-up failed:', error)
    // Return a structured error response instead of throwing
    return {
      success: false,
      message: 'Failed to create account. Please try again.'
    }
  }
}

export const completeSignup = async (data: FormData) => {
  try {
    // console.log('📡 Auth Service: completeSignup called')
    // Log FormData contents
    const formDataObj: Record<string, FormDataEntryValue> = {}
    data.forEach((value, key) => {
      formDataObj[key] = value
    })
    // console.log('📡 Auth Service: FormData contents:', formDataObj)

    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post('/auth/complete-signup', data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      withCredentials: true
    })
    // console.log('📡 Auth Service: completeSignup response:', response.data)
    return response.data
  } catch (error: unknown) {
    // console.error('📡 Auth Service: Complete signup error:', error)
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const setUserRegionOnLocalStorage = async (region: string) => {
  localStorage.setItem(STORAGE_KEYS.USER_REGION, region)
}

export const getUserRegionFromLocalStorage = () => {
  return localStorage.getItem(STORAGE_KEYS.USER_REGION)
}

export const getUserDataRegion = async (email: string) => {
  try {
    const userRegion = getUserRegionFromLocalStorage()

    if (userRegion) {
      return userRegion
    }
    if (!email || email.trim() === '') {
      return ENV.AWS_DEFAULT_REGION
    }
    const response = await axios.get(
      `${ENV.AWS_EMAIL_REGION_MAPPER_URL}?email=${email}`
    )

    if (response.data.error) {
      setUserRegionOnLocalStorage(ENV.AWS_DEFAULT_REGION)
      return ENV.AWS_DEFAULT_REGION
    }

    const region = response.data.region || ENV.AWS_DEFAULT_REGION
    setUserRegionOnLocalStorage(region)
    return region
  } catch {
    setUserRegionOnLocalStorage(ENV.AWS_DEFAULT_REGION)
    return ENV.AWS_DEFAULT_REGION
  }
}
export const getUserRegion = async (email: string) => {
  try {
    const response = await axios.get(
      `${ENV.AWS_EMAIL_REGION_MAPPER_URL}?email=${email}`
    )
    if (response.data.error) {
      return ENV.AWS_DEFAULT_REGION
    }
    const region = response.data.region || ENV.AWS_DEFAULT_REGION
    return region
  } catch (error) {
    console.log('Error getting user region', error)
    return ENV.AWS_DEFAULT_REGION
  }
}
export const checkEmailStatus = async (email: string) => {
  try {
    const regionResponse = await axios.get(
      `${ENV.AWS_EMAIL_REGION_MAPPER_URL}?email=${email}`
    )

    if (regionResponse?.data?.region) {
      return {
        success: false,
        type: 'exists',
        message: 'Email is already associated with an existing account'
      }
    }

    const regions = ['us-east-1', 'eu-west-1']

    const promises = regions.map(async region => {
      try {
        const apiClient = await getAxiosApiClientByUser(email, region)
        const url = `/auth/check-email/${email}`
        const response = await apiClient.get(url)
        return { success: true, data: response.data, region }
      } catch (error) {
        console.log(`Failed to check email in region ${region}:`, error)
        return { success: false, error, region }
      }
    })

    const results = await Promise.all(promises)

    const invitedResult = results.find(
      result => result?.data?.type === 'invited'
    )

    if (invitedResult) {
      return invitedResult.data
    }

    const successfulResult = results.find(result => result.success)
    if (successfulResult) {
      return successfulResult.data
    }

    const firstError = results[0]?.error

    // Type guard for axios error
    const isAxiosError = (
      err: unknown
    ): err is {
      response?: { data?: unknown }
      code?: string
      config?: unknown
    } => {
      return (
        typeof err === 'object' &&
        err !== null &&
        ('response' in err || 'code' in err)
      )
    }

    if (isAxiosError(firstError)) {
      // Check if we got HTML instead of JSON (indicates wrong endpoint)
      if (
        firstError.response?.data &&
        typeof firstError.response.data === 'string' &&
        firstError.response.data.includes('<!DOCTYPE html>')
      ) {
        return {
          success: false,
          type: 'error',
          message:
            'API endpoint not accessible. Please check if the FastAPI backend is running and accessible.'
        }
      }

      // Handle network errors
      if (
        firstError.code === 'ECONNREFUSED' ||
        firstError.code === 'ERR_NETWORK'
      ) {
        return {
          success: false,
          type: 'error',
          message:
            'Cannot connect to backend service. Please ensure the FastAPI server is running.'
        }
      }

      // Return a structured error response instead of throwing
      return {
        success: false,
        type: 'error',
        message:
          firstError.response?.data &&
          typeof firstError.response.data === 'object' &&
          firstError.response.data !== null &&
          'detail' in firstError.response.data &&
          typeof firstError.response.data.detail === 'string'
            ? firstError.response.data.detail
            : 'Failed to check email status. Please try again.'
      }
    }

    return {
      success: false,
      type: 'error',
      message: 'An unexpected error occurred'
    }
  } catch {
    // Handle any errors from the region mapper or other parts
    return {
      success: false,
      type: 'error',
      message: 'Failed to check email status. Please try again.'
    }
  }
}

export const checkInvitation = async (email: string) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.get(
      `/user/agency-user/by-invited-email/${email}`
    )
    return response.data
  } catch (error) {
    handleApiError(error, 'Error during checkInvitation')
  }
}

export const resendOTP = async (email: string) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post('/auth/resend-otp', { email })
    return response.data
  } catch (error) {
    handleApiError(error, 'Error during resend OTP')
  }
}

export const verifySignUpOTP = async (data: { email: string; otp: string }) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post('/auth/verify-otp', data)
    return response.data
  } catch (error) {
    handleApiError(error, 'Error during send OTP')
  }
}

export const updatePassword = async (
  oldPassword: string,
  newPassword: string
) => {
  try {
    const payload = {
      old_password: oldPassword,
      new_password: newPassword
    }

    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post(`/auth/update-password`, payload)
    return response.data
  } catch (error) {
    handleApiError(error, 'Error Updating Password')
  }
}

export const resetPasswordByAccessToken = async (
  newPassword: string,
  accessToken: string,
  refreshToken: string
) => {
  try {
    const payload = {
      access_token: accessToken,
      refresh_token: refreshToken,
      passowrd: newPassword
    }

    const apiClient = await getAxiosApiClientByUser()

    const response = await apiClient.post(
      `/auth/update-password-by-access-token`,
      payload
    )
    return response.data
  } catch (error) {
    handleApiError(error, 'Error Updating Password')
  }
}

export const updateEmailByAccessToken = async (
  accessToken: string,
  refreshToken: string
) => {
  try {
    const payload = {
      access_token: accessToken,
      refresh_token: refreshToken
    }

    const apiClient = await getAxiosApiClientByUser()

    const response = await apiClient.put(
      `/auth/update-email-by-access-token`,
      payload
    )
    return response.data
  } catch (error) {
    handleApiError(error, 'Error Updating Password')
  }
}

export const sendResetPasswordEmail = async (email: string) => {
  try {
    const payload = {
      email
    }
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post(`/auth/reset-password`, payload)
    return response.data
  } catch (error) {
    handleApiError(error, 'Error Sending Email')
  }
}

export const updateEmail = async (newEmail: string, password: string) => {
  const payload = {
    new_email: newEmail,
    current_password: password
  }
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post('/auth/update-email', payload)
    return { success: true, data: response }
  } catch (error: unknown) {
    // console.error('Update user info error:', error)
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

//   export const inviteTeamMembers = async (invitations: Invitation[]) => {
//   try {
//     const payload = {
//       invitations,
//     };

//     const apiClient = await getAxiosApiClientByUser()
//     const response = await apiClient.post(`/invitations/send-invite`, payload)
//     return response.data
//   } catch (error) {
//     handleApiError(error, 'Error Sending Invites')
//   }
// }
